import * as React from "react";
import { useTranslation } from "react-i18next";

/**
 * "Delegation, not back-and-forth": the three steps, each as a card with the indicative
 * UI of the step it names — the command box, the negotiation ledger, the delivered
 * receipt. Titles come from {@code how.steps.<key>.title} in the locale bundles; the
 * cards deliberately carry no captions.
 *
 * <p>The insides of the mocks stay in English on purpose: they depict the product's own
 * UI, the same way the Scheda landing's chat mock does, so they read as screenshots
 * rather than as page copy.
 *
 * <p>The three cards step in one after another when the band first comes into view,
 * 150ms apart. The hidden state is attached by script and nowhere else, so with no JS,
 * no IntersectionObserver, or prefers-reduced-motion, all three are simply present.
 */
export function HowItWorks() {
  const { t } = useTranslation();
  const flowRef = React.useRef<HTMLOListElement>(null);
  const [armed, setArmed] = React.useState(false);
  const [shown, setShown] = React.useState(0);

  React.useEffect(() => {
    const flow = flowRef.current;
    if (!flow || !("IntersectionObserver" in window)) return;
    if (typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    setArmed(true);
    const timers: number[] = [];
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          io.disconnect();
          for (let k = 1; k <= 3; k++) {
            timers.push(window.setTimeout(() => setShown(k), (k - 1) * 150));
          }
        });
      },
      { threshold: 0.28 },
    );
    io.observe(flow);
    return () => {
      io.disconnect();
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, []);

  const cardClass = (k: number): string => (shown >= k ? "fcard in" : "fcard");

  return (
    <section id="how" className="band ochre rule-brass">
      <div className="wrap">
        <div className="hgroup" style={{ maxWidth: "62ch" }}>
          <p className="eyebrow">{t("nav.how")}</p>
          <h2 style={{ marginTop: "10px" }}>{t("how.title")}</h2>
        </div>

        {/* An ordered list, as before the redesign: three numbered steps are a
            sequence, and the semantics survive the restyle for free. */}
        <ol className={armed ? "flow seq" : "flow"} ref={flowRef}>
          <li className={cardClass(1)}>
            <div className="fh">
              <div className="fn">01</div>
              <h3>{t("how.steps.command.title")}</h3>
            </div>
            <div className="fart">
              <div className="cmd">
                <div className="box">
                  book the review with Priya and Tom next week<i className="cmdcaret" />
                </div>
                <div className="send">Send</div>
              </div>
              <div className="ftags">
                <span className="ftag blue">Outcome</span>
                <span className="ftag">One agent</span>
                <span className="ftag sun">By Friday</span>
              </div>
            </div>
          </li>

          <li className={cardClass(2)}>
            <div className="fh">
              <div className="fn">02</div>
              <h3>{t("how.steps.negotiate.title")}</h3>
            </div>
            <div className="fart">
              <ul className="fparts">
                <li><span className="fav">Y</span><span className="nm">Your agent</span><span className="fst acc">Offered</span></li>
                <li><span className="fav">T</span><span className="nm">Their agent</span><span className="fst cnt">Countered</span></li>
              </ul>
              <div className="frounds">
                <span className="r done">1</span><span className="r now">2</span><span className="r">3</span>
              </div>
            </div>
          </li>

          <li className={cardClass(3)}>
            <div className="fh">
              <div className="fn">03</div>
              <h3>{t("how.steps.outcome.title")}</h3>
            </div>
            <div className="fart">
              <div className="receipt">
                <div className="h"><i>✓</i> Delivered</div>
                <div className="when">Approved by you</div>
              </div>
              <ul className="fparts" style={{ marginTop: "10px" }}>
                <li><span className="nm">Record</span><span className="fst acc">On the card</span></li>
                <li><span className="nm">Notice</span><span className="fst acc">Sent</span></li>
              </ul>
            </div>
          </li>
        </ol>
      </div>
    </section>
  );
}
